// Polyfills are NOT imported here: src/polyfills.ts is its own entry, emitted as
// a module script before this one in index.html (vingreact#981), so it runs
// before the vendor chunks this entry imports statically.
import React from 'react';
import ReactDOM from 'react-dom/client';
import '@/theme/global.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary, GlobalFailureScreen } from '@/components/errors';
import { onlineManager } from '@tanstack/react-query';
import { ConnectivityBoundary, installConnectivityMonitor, registerRecoveryHook, setOnlineManager, setSlowReportFn } from '@/connectivity';
import type { SlowReportInput } from '@/connectivity';
import { queryClient } from '@/api/queryClient';
import { wireAppQueryClient } from '@/api/serverErrorRouting';
import { TooltipProvider } from '@/components/primitives';
import { registerAuthFailureHook, registerContextRequiredHook } from '@/api/client';
import { contextRequiredHook } from '@/api/contextRequiredHook';
import { installGlobalCrashHandlers, installPreloadErrorReload, reportClientError } from '@/api/clientErrors';
import { clearSession, registerSessionCleanup } from '@/store';
import { AUTH_ME_KEY, installCrossTabUserSync, readSeed } from '@/auth/authData';
import { hasSession, installCrossTabDeauth, isTokenExchangeArrival } from '@/auth/session';
import { App } from './App';

// Wire the central query/mutation error routing onto the shared queryClient.
// Done here (not in queryClient.ts) so the queryClient module stays free of the
// serverErrorRouting import graph — see queryClient.ts.
wireAppQueryClient();

// Install top-level crash handlers BEFORE React mounts, so a crash in
// the initial render — or in the ErrorBoundary component itself — is
// still reported.
installGlobalCrashHandlers();

// Recover from stale-deploy chunk skew (a cached index.html lazy-loading a
// route chunk whose hash no longer exists): reload once to pull the fresh
// index + chunk map. See installPreloadErrorReload.
installPreloadErrorReload();

// Bootstrap the auth/me cache from the persisted seed so a reload paints the
// last-known user instantly (no ProtectedRoute flash), marked already-stale
// (updatedAt: 0) so useAuthQuery refetches immediately on mount for a fresh
// user. The query is created/routed in src/api/queryClient.ts.
// Skipped on a `/sso?auth_token=…` arrival: that token supersedes the stored
// session, so painting the ENDED session's user (which mirrors into the auth
// store) is wrong until `Sso` has redeemed it.
if (hasSession() && !isTokenExchangeArrival()) {
  const seed = readSeed();
  if (seed) {
    queryClient.setQueryData(AUTH_ME_KEY, seed, { updatedAt: 0 });
  }
}

// Cross-tab session sync (security-critical): another tab clearing the session
// token (logout) logs this tab out too, funneled through clearSession() so the
// query cache, staff impersonation, and banners are all wiped. De-auth only —
// a populated localStorage payload is never trusted.
if (typeof window !== 'undefined') {
  installCrossTabDeauth(clearSession);
  installCrossTabUserSync();
}

// Bug-hunt #8, #84 / audit C7: on any session end — 401 auth failure,
// voluntary logout, or cross-tab logout — clear the query cache AND every
// Zustand store so the next user logging in on the same tab can't briefly
// see the previous user's data on first paint. The query cache lives here,
// so it's registered as a session cleanup; all logout paths call
// clearSession().
registerSessionCleanup(() => queryClient.clear());
registerAuthFailureHook(clearSession);

registerContextRequiredHook(contextRequiredHook);

// D2 (#239): inject TanStack's onlineManager so the connectivity controller can
// pause/resume queries on transport failures.
setOnlineManager(onlineManager);

// R7 (#239c): inject the slow-report function so the connectivity controller can
// post one report per slow request without importing from the api layer directly.
setSlowReportFn((input: SlowReportInput) => {
  reportClientError({
    error: new Error(`slow request: ${input.method.toUpperCase()} ${input.route}`),
    source: 'slow',
    kind: 'api',
    name: 'SlowRequest',
    message: `slow request: ${input.method.toUpperCase()} ${input.route}`,
    traceId: input.traceId,
    context: {
      route: input.route,
      method: input.method.toUpperCase(),
      elapsed_ms: input.elapsedMs,
      timeout_ms: input.timeoutMs,
      probe_ms: input.probeMs,
      kind: 'slow',
    },
  });
});

// C5 connectivity: watch the browser online/offline signals, and — when the API
// recovers and the boundary tears down — resume react-query so the app repaints
// with fresh data instead of the stale/errored caches from the outage.
installConnectivityMonitor();
registerRecoveryHook(() => {
  void queryClient.invalidateQueries();
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <App />
          <GlobalFailureScreen />
          <ConnectivityBoundary />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
